import { NgTemplateOutlet, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  TemplateRef,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  input,
  output,
  untracked,
  viewChild
} from '@angular/core';
import { createViewer } from '../../engine/viewer-engine';
import type {
  DocumentContentsVisibleChangeEvent,
  DocumentErrorEvent,
  DocumentFormat,
  DocumentFormatSelectedEvent,
  DocumentLoadedEvent,
  DocumentProgressEvent,
  DocumentSource,
  DocumentView,
  DocumentViewChangeEvent,
  DocumentWordWrapChangeEvent,
  SizeUnits,
  UnknownFileBehavior,
  ViewerMemoryStats
} from '../../models/viewer-types';

const DEFAULT_FILE_BYTES = 30 * 1024 * 1024;
const DEFAULT_PDF_BYTES = 100 * 1024 * 1024;
const DEFAULT_TIMEOUT_MS = 60000;

/** What the engine is built with. A change to any of these makes a new engine. */
interface EngineSettings {
  pdfAssetsUrl: string;
  format: DocumentFormat;
  language: string;
  maxFileBytes: number;
  maxPdfBytes: number;
  maxExpandedBytes: number | null;
  timeoutMs: number;
}

/** What to open, and the settings it is opened under. */
interface OpenRequest {
  source: DocumentSource | null;
  file: File | Blob | null;
  url: string | null;
  fileName: string;
  settings: EngineSettings;
}

const positive = (value: number, fallback: number): number => (Number.isFinite(value) && value > 0 ? value : fallback);

@Component({
  selector: 'mbs-document-viewer',
  standalone: true,
  imports: [NgTemplateOutlet],
  templateUrl: './document-viewer.component.html',
  // The shared sheets are listed rather than pulled in with @use: the anyComponentStyle budget is
  // measured per file, and inlining them put this one 3 kB over the 20 kB the build allows.
  styleUrls: ['../../styles/theme.scss', './document-viewer.component.scss', '../../styles/dialog.scss'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentViewerComponent implements OnDestroy {
  /** Absolute HTTP(S) URL or a path relative to the host application's base URL. */
  readonly url = input<string | null>(null);
  /** A File or Blob takes precedence over url when both inputs are provided. */
  readonly file = input<File | Blob | null>(null);
  readonly fileName = input('');
  readonly source = input<DocumentSource | null>(null);
  /** Override format detection for extensionless URLs and Blobs. */
  readonly format = input<DocumentFormat>('auto');
  /** Optional syntax language for code previews. */
  readonly language = input('');
  readonly unknownFileBehavior = input<UnknownFileBehavior>('prompt');
  readonly view = input<DocumentView>('auto');
  readonly emptyStateTemplate = input<TemplateRef<unknown> | null>(null);
  readonly contentsVisible = input(false);
  readonly wordWrap = input(false);
  readonly sizeUnits = input<SizeUnits>('binary');
  /** Limit for Office documents, text, and images. */
  readonly maxFileBytes = input(DEFAULT_FILE_BYTES);
  /** Independent PDF limit, including range-loaded and local files. */
  readonly maxPdfBytes = input(DEFAULT_PDF_BYTES);
  /** Optional expanded ZIP limit; null preserves browser-only preview behavior. */
  readonly maxExpandedBytes = input<number | null>(null);
  readonly timeoutMs = input(DEFAULT_TIMEOUT_MS);
  /** Same-origin directory containing the packaged PDF.js assets. */
  readonly pdfAssetsUrl = input('./pdfjs/');
  /** URL of the notices hosted by the consuming application. */
  readonly noticesUrl = input('');
  readonly progressChange = output<DocumentProgressEvent>();
  readonly wordWrapChange = output<DocumentWordWrapChangeEvent>();
  readonly contentsVisibleChange = output<DocumentContentsVisibleChangeEvent>();
  readonly viewChange = output<DocumentViewChangeEvent>();
  readonly formatSelected = output<DocumentFormatSelectedEvent>();
  readonly loaded = output<DocumentLoadedEvent>();
  readonly loadError = output<DocumentErrorEvent>();

  private readonly mount = viewChild<ElementRef<HTMLElement>>('mount');
  private readonly zone = inject(NgZone);
  private readonly platformId = inject(PLATFORM_ID);
  private engine?: ReturnType<typeof createViewer>;
  private destroyed = false;

  private readonly engineSettings = computed<EngineSettings>(() => ({
    pdfAssetsUrl: this.pdfAssetsUrl(),
    format: this.format(),
    language: this.language(),
    maxFileBytes: positive(this.maxFileBytes(), DEFAULT_FILE_BYTES),
    maxPdfBytes: positive(this.maxPdfBytes(), DEFAULT_PDF_BYTES),
    maxExpandedBytes: this.maxExpandedBytes() === null ? null : positive(this.maxExpandedBytes() as number, 0) || null,
    timeoutMs: positive(this.timeoutMs(), DEFAULT_TIMEOUT_MS)
  }));

  private readonly openRequest = computed<OpenRequest>(() => ({
    source: this.source(),
    file: this.file(),
    url: this.url(),
    fileName: this.fileName(),
    settings: this.engineSettings()
  }));

  constructor() {
    // The settings the engine carries are changed on the engine it already has; the rest of them
    // build a new one. Declared in this order so that a change to both reaches the engine first,
    // and so that the imperative calls do not subscribe this effect to everything they read.
    effect(() => {
      const value = this.unknownFileBehavior();

      untracked(() => this.engine?.setUnknownFileBehavior(value));
    });
    effect(() => {
      const value = this.contentsVisible();

      untracked(() => this.engine?.setContentsVisible(value));
    });
    effect(() => {
      const value = this.view();

      untracked(() => this.engine?.setView(value));
    });
    effect(() => {
      const value = this.wordWrap();

      untracked(() => this.engine?.setWordWrap(value));
    });
    effect(() => {
      const value = this.sizeUnits();

      untracked(() => this.engine?.setSizeUnits(value));
    });
    effect(() => {
      const mount = this.mount();
      const settings = this.engineSettings();

      untracked(() => {
        if (mount && isPlatformBrowser(this.platformId)) this.build(mount.nativeElement, settings);
      });
    });
    effect(() => {
      const request = this.openRequest();

      untracked(() => {
        if (this.engine) this.apply(request);
      });
    });
  }

  private build(mount: HTMLElement, settings: EngineSettings): void {
    this.zone.runOutsideAngular(() => {
      this.engine?.destroy();
      this.engine = createViewer(mount, {
        ...settings,
        sizeUnits: this.sizeUnits(),
        wordWrap: this.wordWrap(),
        view: this.view(),
        contentsVisible: this.contentsVisible(),
        unknownFileBehavior: this.unknownFileBehavior(),
        onContentsVisibleChange: (event) => {
          if (!this.destroyed) this.zone.run(() => this.contentsVisibleChange.emit(event));
        },
        onViewChange: (event) => {
          if (!this.destroyed) this.zone.run(() => this.viewChange.emit(event));
        },
        onWordWrapChange: (value) => {
          if (!this.destroyed) this.zone.run(() => this.wordWrapChange.emit(value));
        },
        onFormatSelected: (event) => {
          if (!this.destroyed) this.zone.run(() => this.formatSelected.emit(event));
        },
        onProgress: (event) => {
          if (!this.destroyed) this.zone.run(() => this.progressChange.emit(event));
        },
        onLoaded: (event) => {
          if (!this.destroyed) this.zone.run(() => this.loaded.emit(event));
        },
        onError: (event) => {
          if (!this.destroyed) this.zone.run(() => this.loadError.emit(event));
        }
      });
    });
  }

  private apply(request: OpenRequest): void {
    if (request.source) void this.openSource(request.source);
    else if (request.file) void this.openFile(request.file, request.fileName);
    else if (request.url?.trim()) void this.openUrl(request.url, request.fileName);
    else this.clear();
  }

  /**
   * Opens a document by link. The link is resolved against `document.baseURI`, fetched without
   * cookies, and never kept anywhere after the document is open.
   *
   * @param {string} url Link to the document.
   * @param {string} [fileName] Name to show, when it is not the one in the link.
   * @param {DocumentFormat} [format] Format to open it as, rather than the one the name implies.
   * @param {string} [language] Language to highlight it as.
   * @param {boolean} [automaticFormat] Whether the format was inferred rather than chosen, which
   *   decides which message a format failure gets.
   * @return {Promise<void>} Settles once the document is on screen or has failed.
   */
  openUrl(
    url: string,
    fileName = '',
    format: DocumentFormat = this.format(),
    language = this.language(),
    automaticFormat = format === 'auto'
  ): Promise<void> {
    return this.zone.runOutsideAngular(() => this.engine?.openUrl(url, fileName, format, language, automaticFormat) ?? Promise.resolve());
  }

  openFile(
    file: File | Blob,
    fileName = '',
    format: DocumentFormat = this.format(),
    language = this.language(),
    automaticFormat = format === 'auto'
  ): Promise<void> {
    return this.zone.runOutsideAngular(() => this.engine?.openFile(file, fileName, format, language, automaticFormat) ?? Promise.resolve());
  }

  openSource(source: DocumentSource): Promise<void> {
    return this.zone.runOutsideAngular(() => this.engine?.openSource(source) ?? Promise.resolve());
  }

  /**
   * What the open PDF is holding. These are estimates of the selected buffers for the host's own
   * diagnostics, not a measure of the viewer's or the worker's total memory.
   *
   * @return {ViewerMemoryStats|null} The estimates, or null when the open document is not a PDF.
   */
  getMemoryStats(): ViewerMemoryStats | null {
    return this.engine?.memoryStats() ?? null;
  }

  clear(options: { preserveTextPreferences?: boolean } = {}): void {
    if (!this.destroyed) this.zone.runOutsideAngular(() => this.engine?.clear(options));
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.engine?.destroy();
    this.engine = undefined;
  }
}
